import React, { useState, useEffect, useRef } from "react";
import { IoMdCall } from "react-icons/io";

import { TurningIconState } from "./IconStates";
import DeviceIcon from "./DeviceIcon";
import TurningIcon from "./TurningIcon";
import NavigationMap from "../NavigationMap";
import CarModeIcon from "./CarMode";
import AuxBatteryIcon from "./AuxBatteryIcon";
import BatteryIcon from "./BatteryIcon";
import ProgressBar from "react-bootstrap/ProgressBar";

import AlertMessages from "./AlertMessages";
import GearStatus from "./GearStatus";

import playSoundAlert from "./SoundAlert";

import VehicleDashboard from '@components/VehicleDashboard/VehicleDashboard';

function formatSpeed(speed) {
  if (speed < 9.99999) {
    return speed.toFixed(1);
  } else {
    return speed.toFixed(0);
  }
}

//
// telemetryDataChannel or alertDataChannel could be null
//
export default function TelemetryStream({
  telemetryDataChannel,
  alertDataChannel,
  hidden,
  vehicle,
  controlUpdate,
  setMechanicalState,
  sendToInfrastructure,
  voiceCallState,
  listOfAvailableVehicles,
}) {
  const [data, setData] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [devices, setDevices] = useState({});
  const lastData = useRef();

  useEffect(() => {
    function mapRange(valueIn, startIn, endIn, startOut, endOut) {
      const scale = (endOut - startOut) / (endIn - startIn);
      return startOut + (valueIn - startIn) * scale;
    }

    function mps2kmph(value) {
      return (value * (60.0 * 60.0)) / 1000.0;
    }

    function rad2deg(value) {
      return (value * 180.0) / 3.14159;
    }

    function parseBuffer(buffer) {
      const floats = new Float32Array(buffer, 0, 8);
      const ints = new Uint8Array(buffer, 8 * 4, 2);
      const data = {
        linearSpeed: floats[0], 
        angularSpeed: floats[1], 
        linearSpeedLimit: floats[2], 
        angularSpeedLimit: floats[3], 
        rpm: floats[4],// Engine rotations per minute [tr/min]
        roll: rad2deg(floats[5]),// Roll of the vehicle [rad]
        pitch: rad2deg(floats[6]),// Pitch of the vehicle [rad]
        fuelPercentage: floats[7],// Fuel level [%]
        
        enlargment : (ints[0] >> 0) & 0x1, // True when the tracks are enlarged, false otherwise
        ignition : (ints[0] >> 1) & 0x1, // True when the ignition key is set, false otherwise
        engine : (ints[0] >> 2) & 0x1, // True when the engine is on, false otherwise
        tool : (ints[0] >> 3) & 0x3,
        toolDirection : (ints[0] >> 5) & 0x1,// True when the tool is turning clockwise, false when counter-clockwise
        toolFloating : (ints[0] >> 6) & 0x1,// True when the tool is flotting, false otherwise
        danger : (ints[0] >> 7) & 0x1, // True when the vehicle is in danger, false otherwise

        lights : (ints[1] >> 0) & 0x1, // True when lights are on, false otherwise
        emergencyStopButton : (ints[1] >> 1) & 0x1, // True when emergency stop (hand) is pressed
        electricAux : (ints[1] >> 2) & 0x1 // True when the vehicle is in closed loop control
        };

      lastData.current = data;
      setData(data);

      // Update mechanical state.
      if (setMechanicalState) {
        setMechanicalState({
          steeringWheel: data.steeringWheel,
          frontWheelsAngle: data.frontWheelsAngle,
          rearWheelsAngle: data.rearWheelsAngle,
          wheelMode: data.wheelMode,
          gear: data.gear,
        });
      }
    }

    if (telemetryDataChannel) {
      telemetryDataChannel.onmessage = function (event) {
        parseBuffer(event.data);
      };
    }
  }, [telemetryDataChannel, controlUpdate, setMechanicalState, setData]);

  useEffect(() => {
    if (alertDataChannel) {
      alertDataChannel.onmessage = function (event) {
        const alert = JSON.parse(String.fromCharCode.apply(null, new Uint8Array(event.data)));
        //console.log(alert);
        setAlerts(alert.alerts);
        setDevices(alert.devices);
        playSoundAlert(alert.alerts);
      };
    }
  }, [alertDataChannel, setAlerts]);

  function sendDeliveryReadMessage() {
    // TODO: call external API to inform the customer of its delivery.
    alert(
      "The customer would have received a message soon, but this function needs to send a call an external API for it to happen."
    );
  }

  function initiateVoiceCallVui() {
    sendToInfrastructure("infrastructure", "forceCall", vehicle.id);
  }

  if (!data) {
    return <div>No telemetry information have been received yet.</div>;
  }

  let gear = "";
  switch (data.gear) {
    case 1:
      gear = "D";
      break;
    case 2:
      gear = "N";
      break;
    case 3:
      gear = "R";
      break;
    default:
      gear = "undefined";
  }

  let wheelMode = "";
  switch (data.wheelMode) {
    case 1:
      wheelMode = "opposite";
      break;
    case 2:
      wheelMode = "regular";
      break;
    case 3:
      wheelMode = "same";
      break;
    default:
      wheelMode = "regular";
  }
  if (hidden) {
    return null;
  } else {
    return (
      <>
        <VehicleDashboard listOfAvailableVehicles={listOfAvailableVehicles} vehicle={vehicle} alerts={alerts} telemetryData={data} devices={devices}/>
      </>
    );
    return (
      <>
        <div
          className="col-4 text-center"
          style={{ display: "flex", flexDirection: "column", alignItems: "center" }}
        >
          <button className="btn btn-outline-primary" onClick={sendDeliveryReadMessage}>
            Send message to customer
          </button>
          
          {/*<button
            style={{ marginTop: 10 }}
            className="btn btn-success"
            onClick={initiateVoiceCallVui}
            disabled={
              !voiceCallState?.vuiOfVehicles?.[vehicle.id] ||
              voiceCallState?.inCallWithOperator?.length
            }
          >
            <IoMdCall size="25px" style={{ marginRight: 5 }} color="white" />
            Voice Call {voiceCallState?.vuiOfVehicles?.[vehicle.id] || "VUI"}
          </button>*/ /* Only used for loxo for now, should be implemented later*/}
        </div>

        <div className="col-12">
          {/**/}
          <hr className="hr-icons" style={{ height: 2 }} />
          <div className="row justify-content-center icons-container">
            <div className="col p-0 my-auto">
              <GearStatus gear={gear} wheelMode={wheelMode} />
            </div>
            <div className="col p-0 my-auto">
              <CarModeIcon
                state={"teleoperated"}
                steeringWheel={data.steeringWheel}
                frontWheelsAngle={data.frontWheelsAngle}
              />
            </div>
            <div className="col-5 my-auto">
              <TurningIcon
                state={
                  data.warningLights
                    ? TurningIconState.BOTH
                    : data.leftTurningLight
                    ? TurningIconState.LEFT
                    : data.rightTurningLight
                    ? TurningIconState.RIGHT
                    : TurningIconState.INACTIVE
                }
              />
            </div>
            <div className="col p-0 auxBattery">
              <AuxBatteryIcon devices={devices.auxBattery} data={data} />
            </div>
            <BatteryIcon data={data} />
          </div>

          <div className="row">
            <div className="col p-0">
              <ProgressBar
                className="brake-progress-bar"
                now={100 - data.brake}
                style={{ height: 2 }}
              />
            </div>
            <div className="col p-0">
              <ProgressBar
                className="throttle-progress-bar"
                now={data.throttle}
                style={{ height: 2 }}
              />
            </div>
          </div>

          <div className="row">
            <div className="col p-0 my-auto">
              <p className="speed speedValue"> {formatSpeed(Math.abs(data.speed /* *1.2 */))} </p>
              <p className="speed speedUnit"> km/h </p>
            </div>

            <div className="col-md-6">
              <div className="row icons-container pt-2">
                <div className="col p-0 my-auto">
                  <DeviceIcon device={devices.radar} />
                </div>
                <div className="col p-0 my-auto">
                  <DeviceIcon device={devices.gnss} />
                </div>
                <div className="col p-0 my-auto">
                  <DeviceIcon
                    device={{
                      name: "positionBeam",
                      status: data.headlight ? "on" : "on",
                      hideStatus: "off",
                    }}
                  />
                </div>
                <div className="col p-0 my-auto">
                  <DeviceIcon
                    device={{
                      name: "lowBeam",
                      status: data.lowBeamLight ? "on" : "off",
                      hideStatus: "off",
                    }}
                  />
                </div>
                <div className="col p-0 my-auto">
                  <DeviceIcon
                    device={{
                      name: "highBeam",
                      status: data.headlight ? "on" : "off",
                      hideStatus: "off",
                    }}
                  />
                </div>
                <div className="col p-0 my-auto">
                  <DeviceIcon
                    device={{ name: "horn", status: data.horn ? "on" : "off", hideStatus: "off" }}
                  />
                </div>
                <div className="col p-0 my-auto">
                  <DeviceIcon
                    device={{
                      name: "brake",
                      status: data.parkingBrake ? "on" : "off",
                      hideStatus: "off",
                    }}
                  />
                </div>
              </div>
              <div className="row">
                <hr className="hr-warnings" style={{ height: 3 }}></hr>
              </div>
              <div className="row alerts">
                <AlertMessages alerts={alerts} />
              </div>
            </div>
            <div className="col p-0 my-auto">
              <p className="speed speedValueMax">
                {" "}
                {formatSpeed(
                  data.gear === 3 ? data.reverseSpeedLimit : data.forwardSpeedLimit
                )}/{formatSpeed(
                  data.gear === 3 ? data.reverseSpeedLimitRemote : data.forwardSpeedLimitRemote
                )}{" "}
              </p>
              <p className="speed speedTextMax" style={{ color: "blue" }}>
                {" "}
                MAX{" "}
              </p>
            </div>
          </div>

          <div className="row pt-3">
            <NavigationMap vehicle={vehicle} isReadOnly={true} />
          </div>
        </div>
      </>
    );
  }
}
